import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import { notificationService } from '@services/api/notifications'
import toast from 'react-hot-toast'

// Formate une date en temps relatif (maintenant, 27min, 2h, 3j, 04 juin)
function formatRelativeTime(d) {
  if (!d) return ''
  const date = new Date(d)
  if (isNaN(date)) return ''
  const diff = Date.now() - date
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const dy = Math.floor(diff / 86400000)
  if (m < 1) return 'maintenant'
  if (m < 60) return `${m}min`
  if (h < 24) return `${h}h`
  if (dy < 7) return `${dy}j`
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

function NotificationsPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, unread, read

  // Charger les notifications depuis l'API
  const loadNotifications = async (silent = false) => {
    if (!user?.client_id) return
    
    try {
      if (!silent) setLoading(true)
      const response = await notificationService.getNotifications(user.client_id, filter)
      if (response.success) {
        setNotifications(response.data)
      }
    } catch (error) {
      console.error('Erreur chargement notifications:', error)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    loadNotifications()
    
    // Rafraîchir toutes les 10 secondes
    const interval = setInterval(() => {
      loadNotifications(true)
    }, 10000)
    
    return () => clearInterval(interval)
  }, [user?.client_id, filter])

  const handleMarkAsRead = async (id) => {
    try {
      const response = await notificationService.markAsRead(id)
      if (response.success) {
        setNotifications(notifications.map(notif =>
          notif.id === id ? { ...notif, is_read: true } : notif
        ))
        // Mettre à jour le badge du header
        window.dispatchEvent(new Event('reloadNotifications'))
      }
    } catch (error) {
      console.error('Erreur marquage comme lu:', error)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationService.markAllRead()
      if (response.success) {
        setNotifications(notifications.map(notif => ({ ...notif, is_read: true })))
        toast.success('Toutes les notifications ont été marquées comme lues')
        // Mettre à jour le badge du header
        window.dispatchEvent(new Event('reloadNotifications'))
      }
    } catch (error) {
      console.error('Erreur marquage tout comme lu:', error)
    }
  }

  const handleDeleteNotification = async (id) => {
    try {
      const response = await notificationService.deleteNotification(id)
      if (response.success) {
        setNotifications(notifications.filter(notif => notif.id !== id))
        toast.success('Notification supprimée')
        window.dispatchEvent(new Event('reloadNotifications'))
      }
    } catch (error) {
      console.error('Erreur suppression notification:', error)
    }
  }

  const handleNotificationClick = async (notif) => {
    await handleMarkAsRead(notif.id)
    if (notif.link) {
      navigate(notif.link)
    }
  }

  const filteredNotifications = notifications

  const unreadCount = notifications.filter(n => !n.is_read).length

  // Icône SVG + couleurs selon le type de notification
  const renderIcon = (type) => {
    const config = {
      like: {
        bg: 'bg-red-100', text: 'text-red-500',
        path: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
      },
      comment: {
        bg: 'bg-blue-100', text: 'text-blue-500',
        path: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
      },
      new_post: {
        bg: 'bg-yellow-100', text: 'text-[#D4AF37]',
        path: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
      },
    }
    const c = config[type] || {
      bg: 'bg-gray-100', text: 'text-gray-500',
      path: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    }
    return (
      <div className={`w-12 h-12 ${c.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
        <svg className={`w-6 h-6 ${c.text}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d={c.path} />
        </svg>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-[#001F3F] mb-1">Notifications</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-gray-600">
                  Vous avez <span className="font-semibold text-[#D4AF37]">{unreadCount}</span> notification{unreadCount > 1 ? 's' : ''} non lue{unreadCount > 1 ? 's' : ''}
                </p>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="px-4 py-2 text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-colors font-medium"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>

          {/* Filtres */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                filter === 'all'
                  ? 'bg-[#D4AF37] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Toutes ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                filter === 'unread'
                  ? 'bg-[#D4AF37] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Non lues ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                filter === 'read'
                  ? 'bg-[#D4AF37] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Lues ({notifications.length - unreadCount})
            </button>
          </div>
        </div>

        {/* Liste des notifications */}
        <div className="space-y-3">
          {loading ? (
            // Skeleton loader
            [...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))
          ) : filteredNotifications.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="text-gray-500 mb-2">Aucune notification</p>
              <p className="text-sm text-gray-400">
                {filter === 'unread' ? 'Vous avez tout lu !' : 'Vous n\'avez aucune notification'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notif) => (
              <div
                key={notif.id}
                className="bg-white rounded-xl overflow-hidden hover:shadow-md transition-all"
              >
                <div className="p-4 flex items-start gap-4">
                  {renderIcon(notif.type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-[#001F3F]">{notif.title}</h3>
                      {!notif.is_read && (
                        <span className="w-2 h-2 bg-[#D4AF37] rounded-full flex-shrink-0 mt-1"></span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{notif.message}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{formatRelativeTime(notif.timestamp || notif.created_at)}</span>
                      <div className="flex items-center gap-2">
                        {notif.link && (
                          <button
                            onClick={() => handleNotificationClick(notif)}
                            className="text-xs text-[#D4AF37] hover:underline font-medium"
                          >
                            Voir
                          </button>
                        )}
                        {!notif.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(notif.id)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Marquer comme lu
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteNotification(notif.id)}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Message si aucune notification filtrée */}
        {!loading && filteredNotifications.length === 0 && notifications.length > 0 && (
          <div className="bg-white rounded-xl p-8 text-center">
            <p className="text-gray-500">Aucune notification dans cette catégorie</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationsPage
